/**
 * Plan Version Diff Models
 *
 * Types and interfaces for comparing two plan versions and calculating differences.
 * Used by the PlanVersionDiffService and consumed by UI components like:
 * - Stats widget (top summary)
 * - Diff sidebar (detailed change list)
 * - Planning grid cells (diff state highlighting)
 */
import { formatCustom, resolveLocaleFromLanguage, SYSTEM_FALLBACK } from '@zag/core/common';

/**
 * Type of difference detected for a cell or entry
 */
export type DiffType = 'added' | 'removed' | 'modified';

/**
 * Difference information for a single grid cell (employee + day combination)
 */
export interface CellDiff {
  /** Employee ID for this cell */
  employeeId: string;
  /** Day key in ISO format (YYYY-MM-DD) */
  dayKey: string;
  /** Type of change detected */
  diffType: DiffType;
  /** Shift IDs in the base (old) version */
  baseShiftIds: string[];
  /** Shift IDs in the compare (new) version */
  compareShiftIds: string[];
  /** Shift IDs that were added */
  addedShiftIds: string[];
  /** Shift IDs that were removed */
  removedShiftIds: string[];
}

/**
 * A single change item for display in the sidebar change list
 */
export interface ChangeItem {
  /** Type of change */
  type: DiffType;
  /** Employee ID */
  employeeId: string;
  /** Employee display name */
  employeeName: string;
  /** Day key in ISO format */
  dayKey: string;
  /** Day name for display (e.g., "Monday", "Tuesday") */
  dayName: string;
  /** Human-readable description of the change */
  description: string;
  /** Shift ID(s) involved in this change */
  shiftIds: string[];
  /** Shift name(s) for display */
  shiftNames: string[];
}

/**
 * Comparison of a single metric between two versions
 */
export interface MetricComparison {
  /** Display label for the metric */
  label: string;
  /** Value in the base (old) version */
  baseValue: number;
  /** Value in the compare (new) version */
  compareValue: number;
  /** Unit for display (e.g., "h", "%", "shifts") */
  unit: string;
  /** Absolute change (compareValue - baseValue) */
  change: number;
  /** Percentage change */
  changePercent: number;
  /** Direction of change for styling */
  changeDirection: 'positive' | 'negative' | 'neutral';
}

/**
 * Summary statistics for the overall diff
 */
export interface DiffSummary {
  /** Total number of cell changes */
  totalChanges: number;
  /** Number of cells with added shifts */
  addedCount: number;
  /** Number of cells with removed shifts */
  removedCount: number;
  /** Number of cells with modified shifts (both added and removed) */
  modifiedCount: number;
  /** Total shifts added across all cells */
  totalShiftsAdded: number;
  /** Total shifts removed across all cells */
  totalShiftsRemoved: number;
  /** Net change in shift count */
  netShiftChange: number;
  /** Metric comparisons for display */
  metrics: MetricComparison[];
}

/**
 * Complete diff result between two plan versions
 */
export interface PlanVersionDiff {
  /** ID of the base (old) plan version */
  basePlanVersionId: string;
  /** ID of the compare (new) plan version */
  comparePlanVersionId: string;
  /** Summary statistics */
  summary: DiffSummary;
  /** Map of cell diffs keyed by "employeeId:dayKey" */
  cellDiffs: Map<string, CellDiff>;
  /** List of individual changes for sidebar display */
  changes: ChangeItem[];
  /** Whether there are any differences */
  hasDifferences: boolean;
}

/**
 * Input data for calculating a plan version diff.
 * Includes the raw plan version data plus lookup maps for generating
 * human-readable descriptions.
 */
export interface PlanVersionDiffInput {
  /** Base (old) plan version ID */
  basePlanVersionId: string;
  /** Compare (new) plan version ID */
  comparePlanVersionId: string;
  /** Base plan version data: date -> employeeId -> shiftIds[] */
  baseData: Record<string, Record<string, string[]>>;
  /** Compare plan version data: date -> employeeId -> shiftIds[] */
  compareData: Record<string, Record<string, string[]>>;
  /** Map of employee ID to employee info for display names */
  employeeMap: Map<string, { firstName: string; lastName: string }>;
  /** Map of shift ID to shift info for display names and times */
  shiftMap: Map<string, { name: string | null; startTime: string; endTime: string }>;
}

/**
 * Creates a cell key from employee ID and day key
 */
export function createCellKey(employeeId: string, dayKey: string): string {
  return `${employeeId}:${dayKey}`;
}

/**
 * Parses a cell key back into employee ID and day key.
 *
 * Supports three formats:
 * - "employeeId:weekday:N" (legacy weekday-keyed)
 * - "employeeId:w{W}:weekday:{D}" (multi-week weekday-keyed)
 * - "employeeId:YYYY-MM-DD" (date-keyed)
 */
export function parseCellKey(cellKey: string): { employeeId: string; dayKey: string } | null {
  // Check for multi-week weekday key format (employeeId:w{W}:weekday:{D})
  const multiWeekMatch = /^(.+):(w[0-3]:weekday:[0-6])$/.exec(cellKey);
  if (multiWeekMatch) {
    return { employeeId: multiWeekMatch[1], dayKey: multiWeekMatch[2] };
  }

  // Check for legacy weekday key format (employeeId:weekday:N)
  const weekdayMatch = /^(.+):(weekday:[0-6])$/.exec(cellKey);
  if (weekdayMatch) {
    return { employeeId: weekdayMatch[1], dayKey: weekdayMatch[2] };
  }

  // Check for date key format (employeeId:YYYY-MM-DD)
  const dateMatch = /^(.+):(\d{4}-\d{2}-\d{2})$/.exec(cellKey);
  if (dateMatch) {
    return { employeeId: dateMatch[1], dayKey: dateMatch[2] };
  }

  return null;
}

/**
 * Array of day names indexed by ISO weekday (0=Monday, 6=Sunday)
 */
const ISO_WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Gets the day name from a day key using the active locale.
 *
 * Supports three formats:
 * - Legacy weekday key: "weekday:N" where N is 0-6 (ISO: 0=Monday, 6=Sunday)
 * - Multi-week weekday key: "w{W}:weekday:{D}" — returns day name (same as legacy)
 * - ISO date string: "YYYY-MM-DD" — uses Intl.DateTimeFormat with the resolved locale
 *
 * @param dayKey - A day key or ISO date string
 * @param language - A supported language code or BCP-47 locale. Defaults to system fallback.
 */
export function getDayName(dayKey: string, language?: string): string {
  // Check for multi-week weekday key (w{W}:weekday:{D})
  const multiWeekMatch = /^w[0-3]:weekday:([0-6])$/.exec(dayKey);
  if (multiWeekMatch) {
    return ISO_WEEKDAY_NAMES[Number.parseInt(multiWeekMatch[1], 10)];
  }

  // Check for legacy weekday key (weekday:N)
  const weekdayMatch = /^weekday:([0-6])$/.exec(dayKey);
  if (weekdayMatch) {
    return ISO_WEEKDAY_NAMES[Number.parseInt(weekdayMatch[1], 10)];
  }

  // Otherwise treat as ISO date string
  const locale = language ? resolveLocaleFromLanguage(language) : SYSTEM_FALLBACK.locale;
  return formatCustom(dayKey, { ...SYSTEM_FALLBACK, locale }, { weekday: 'long' });
}

/**
 * Formats a date for display using the active locale.
 *
 * Supports three formats:
 * - Legacy weekday key: "weekday:N" - returns the day name (e.g., "Monday")
 * - Multi-week weekday key: "w{W}:weekday:{D}" - returns the day name
 * - ISO date string: "YYYY-MM-DD" - returns short date (e.g., "Nov 9" or "9. Nov.")
 *
 * @param dayKey - A day key or ISO date string
 * @param language - A supported language code or BCP-47 locale. Defaults to system fallback.
 */
export function formatDateShort(dayKey: string, language?: string): string {
  // Check if it's a weekday key (legacy or multi-week) - return the day name for templates
  if (dayKey.startsWith('weekday:') || /^w[0-3]:weekday:[0-6]$/.test(dayKey)) {
    return getDayName(dayKey, language);
  }

  // Otherwise treat as ISO date string
  const locale = language ? resolveLocaleFromLanguage(language) : SYSTEM_FALLBACK.locale;
  return formatCustom(dayKey, { ...SYSTEM_FALLBACK, locale }, { month: 'short', day: 'numeric' });
}
